/** Состояние игрока: ответ входа и любой операции, меняющей прогресс. */
import { BalanceVersion } from "../../balance";
import { Coins, ContentId, HeroId, Locale, PosInt, Seed, UInt, UnixMs } from "../../common";
import {
  RetryLock,
  ReviveOffer,
  RevivePicks,
  StarPrices,
  StarterOffer,
  VipState,
  starPrices,
} from "../../monetization";
import { RunState } from "../../wave-log";
import { StreakState, progressFields } from "./progress";
import { BetaState } from "./beta";
import { leagueFields } from "./league";
import { seasonFields } from "./season";
import { z } from "zod";

/**
 * Рука A/B-теста масштаба поля (MADR 0036): `a` — камера как была, `b` — поле дальше в 1,4 раза, всё
 * мельче. Только отрисовка: бой, журнал волны и проверка сервера от неё не зависят.
 */
export const FieldScale = z.enum(["a", "b"]);
export type FieldScale = z.infer<typeof FieldScale>;

/** Профиль игрока. */
export const PlayerProfile = z.object({
  /** Внутренний id игрока (UUID). */
  id: z.uuid(),
  /** Отображаемое имя (из Telegram или «Guest»). */
  name: z.string().max(64),
  /** Telegram user id; `null` — гость в браузере. */
  telegramId: z.number().int().positive().nullable(),
  locale: Locale,
  /** Игрок выбрал язык сам: он важнее языка Telegram и браузера. */
  localeChosen: z.boolean(),
  /**
   * Может ли бот писать игроку: `null` — ещё не спрашивали, `false` — отказал или заблокировал бота, снова не спрашиваем.
   * Без поля (старый сервер во время выката) — `null`.
   */
  allowsWriteToPm: z.boolean().nullable().default(null),
  /** Владелец проверяет игру инструментами `/admin`: аналитику клиент не шлёт. */
  testAccount: z.boolean().default(false),
  /** Инструменты ускорения из `ADMIN_TELEGRAM_IDS` (MADR admin-speed): решает сервер, он же проверяет каждый запрос. */
  adminTools: z.boolean().default(false),
  /** Тестовый сброс: клиент очищает флаги первых показов, если этот сброс ещё не видел. */
  testResetAt: UnixMs.nullable().default(null),
  /** Рука A/B-теста масштаба поля; по умолчанию у всех `a`, `b` — владелец вручную или режим «50/50». */
  fieldScale: FieldScale.default("a"),
  /** Когда создан аккаунт. */
  createdAt: UnixMs,
});
export type PlayerProfile = z.infer<typeof PlayerProfile>;

/** Прогресс героя (вкладки Heroes / Level / Stars). */
export const HeroProgress = z.object({
  heroId: HeroId,
  /** Открыт (дошёл до волны открытия хотя бы раз). */
  unlocked: z.boolean(),
  level: PosInt,
  stars: z.number().int().min(0).max(5),
});
export type HeroProgress = z.infer<typeof HeroProgress>;

/** Офлайн-доход, ожидающий получения (окно «с возвращением»). */
export const OfflineIncome = z.object({
  /** Монет к получению; 0 — окно не показывать. */
  coins: Coins,
  /** Сколько секунд игрок отсутствовал (с учётом потолка). */
  seconds: UInt,
  /** Текущий потолок, секунд (8 ч + Tech). */
  capSeconds: UInt,
});
export type OfflineIncome = z.infer<typeof OfflineIncome>;

/** Следующая волна: номер и сид, выданный сервером (клиент не выбирает сид сам — нельзя «перебрать» удачный). */
export const NextWave = z.object({
  wave: PosInt,
  seed: Seed,
});
export type NextWave = z.infer<typeof NextWave>;

/** Полное состояние игрока — ответ входа и любой операции, меняющей прогресс. */
export const PlayerState = z.object({
  player: PlayerProfile,
  /** Подтверждённые монеты. */
  coins: Coins,
  heroes: z.array(HeroProgress).min(5).max(9),
  /** Уровни Tech: `techId → уровень`. */
  tech: z.record(ContentId, UInt),
  /** Текущая забежка (всегда есть: после проигрыша/выхода начинается новая). */
  run: RunState,
  next: NextWave,
  /** Лучшая волна за всё время. */
  bestWave: UInt,
  streak: StreakState,
  offline: OfflineIncome,
  /** VIP: считает сервер по журналу платежей (MADR 0003). */
  vip: VipState,
  /** Таймер после падения штаба; `null` — бой можно начинать. */
  retryLock: RetryLock.nullable(),
  /** Предложение реванша (MADR 0007); `null` — реванш недоступен. */
  revive: ReviveOffer.nullable().default(null),
  /** Подкрепление после реванша (MADR 0019): пока не `null`, бой ждёт выбора. Без поля (старый сервер) — `null`. */
  revivePicks: RevivePicks.nullable().default(null),
  /** Стартовый пакет (MADR 0014); `null` — уже показан, куплен, VIP активен или игрок не из Telegram. */
  starter: StarterOffer.nullable().default(null),
  /** Цены, с которыми сервер выпишет счёт. Без поля (старый сервер) — обычные. */
  prices: StarPrices.default(starPrices(false)),
  /** Версия баланса, которой ждёт сервер. Не совпала с клиентской — клиент перезагружается. */
  balanceVersion: BalanceVersion,
  serverTime: UnixMs,
  /** Бета (`X-Game-Mode: beta`); в обычном режиме поля нет. */
  beta: BetaState.optional(),
  ...progressFields,
  ...seasonFields,
  ...leagueFields,
});
export type PlayerState = z.infer<typeof PlayerState>;
